import {Component,Input,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {Prestation} from "@domain/prestation/prestation";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {first} from "rxjs/operators";
import {Referentiel} from "@domain/admin/referentiels/referentiel";
import {TranslateService} from "@ngx-translate/core";
import {DisplayCompte} from "@domain/comptabilite/compte";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {AddCompteChargeComponent} from "@components/admin/frais/prestations/prestation-detail/generalites/add-compte-charge.component";
import {Field,FieldContainer} from "@domain/settings/field";
import {NgForm} from "@angular/forms";
import {Observable,Subscription} from "rxjs";
import {CustomInputComponent} from "@share/component/custom-input/custom-input.component";

/**
 * Composant de l'onglet généralités d'une prestation
 */
@Component({
	selector: 'prestation-generalites',
	host: {'data-test-id': 'prestation-generalites'},
	templateUrl: './prestation-generalites.component.html'
})
export class PrestationGeneralitesComponent implements OnInit, OnDestroy {
	/** Constante */
	public static readonly OBJET_TAMPON: string = "objetTampon";

	/** Prestation concernée */
	private _prestation: Prestation;
    @Input() set prestation(prestation: Prestation) { this._prestation = prestation; this.syncPrestation(); };
	get prestation(): Prestation { return this._prestation; }

	/** Flag indiquant si la page est chargée */
	@Input() isLoaded: boolean;

	/** Liste des icônes possibles pour le select */
	listeIcones: Array<any> = [
		{type: "spoke",libelle: "spoke",icone: '<i class="material-icons-outlined warning align-middle">spoke</i>' },
		{type: "help_outline",libelle: "help_outline",icone: '<i class="material-icons-outlined warning align-middle">help_outline</i>' },
		{type: "restaurant_menu",libelle: "restaurant_menu",icone: '<i class="material-icons-outlined warning align-middle">restaurant_menu</i>' },
		{type: "restaurant",libelle: "restaurant",icone: '<i class="material-icons-outlined warning align-middle">restaurant</i>' },
		{type: "local_hotel",libelle: "local_hotel",icone: '<i class="material-icons-outlined warning align-middle">local_hotel</i>' },
		{type: "local_parking",libelle: "local_parking",icone: '<i class="material-icons-outlined warning align-middle">local_parking</i>' },
		{type: "airport_shuttle",libelle: "airport_shuttle",icone: '<i class="material-icons-outlined warning align-middle">airport_shuttle</i>' },
		{type: "directions_car",libelle: "directions_car",icone: '<i class="material-icons-outlined warning align-middle">directions_car</i>' },
		{type: "local_taxi",libelle: "local_taxi",icone: '<i class="material-icons-outlined warning align-middle">local_taxi</i>' },
		{type: "local_car_wash",libelle: "local_car_wash",icone: '<i class="material-icons-outlined warning align-middle">local_car_wash</i>' },
		{type: "local_airport",libelle: "local_airport",icone: '<i class="material-icons-outlined warning align-middle">local_airport</i>' },
		{type: "train",libelle: "train",icone: '<i class="material-icons-outlined warning align-middle">train</i>' },
		{type: "local_gas_station",libelle: "local_gas_station",icone: '<i class="material-icons-outlined warning align-middle">local_gas_station</i>' },
		{type: "shopping_cart",libelle: "shopping_cart",icone: '<i class="material-icons-outlined warning align-middle">shopping_cart</i>' },
		{type: "streetview",libelle: "streetview",icone: '<i class="material-icons-outlined warning align-middle">streetview</i>' },
		{type: "local_cafe",libelle: "local_cafe",icone: '<i class="material-icons-outlined warning align-middle">local_cafe</i>' },
		{type: "av_timer",libelle: "av_timer",icone: '<i class="material-icons-outlined warning align-middle">av_timer</i>' },
		{type: "group",libelle: "group",icone: '<i class="material-icons-outlined warning align-middle">group</i>' },
		{type: "phone",libelle: "phone",icone: '<i class="material-icons-outlined warning align-middle">phone</i>' }
	];

	/** Liste des familles possibles */
	listeFamille: Referentiel[] = [{
		id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucune")
	} as Referentiel];

	/** Famille sélectionnée */
	selectedFamille: Referentiel;

	/** Liste des types de prestations possibles */
	listeTypePrestation: Referentiel[] = [{
		id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucun")
	} as Referentiel];

	/** Type de prestation sélectionné */
	selectedTypePrestation: Referentiel;

	/** Liste des comptes charge possibles */
	listeCompteCharge: DisplayCompte[] = [];

	/** Compte charge sélectionné */
	selectedCompteCharge: DisplayCompte;

	/** Liste des codes liasses possibles */
	listeCodeLiasse: Referentiel[] = [{
		id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucun")
	} as Referentiel];

	/** Code liasse sélectionné */
	selectedCodeLiasse: Referentiel;

	/** Wrapper pour les champs attribut */
	@Input() fieldContainer: FieldContainer;

	/** Évènement de rechargement des données de la page */
	@Input() reloadPageDataEvent: Observable<void>;

	/** Champs attribut */
	listeFieldParam: Field[];

	/** Formulaire du composant */
	@ViewChild('form') form: NgForm;

	/** Custom inputs 'objectselect' */
	@ViewChild('famille') famille: CustomInputComponent;
	@ViewChild('type') type: CustomInputComponent;
	@ViewChild('liasse') liasse: CustomInputComponent;

	/** Souscription */
	souscription: Subscription;

	/**
	 * Constructeur
	 */
	constructor(private prestationService: PrestationService,private translateService: TranslateService,private matDialog: MatDialog) {}

	/**
	 * Initialisation du composant
	 */
	ngOnInit() {
		//Chargement des données de la page
		this.loadPageData();

		//Abonnement au rechargement des données de la page
		this.souscription = this.reloadPageDataEvent?.subscribe(() => {
			//Rechargement des données de la page
			this.loadPageData();

			//Purge des custom inputs
			this.cleanCustomInputs();
		});

		//Récupération des zones utilisateurs prédéfinies
		this.prestationService.listeZoneUtilisateurPredefinie().pipe(first()).subscribe(f => this.listeFieldParam = f);
	}

	/**
	 * Chargement des données de la page
	 */
	loadPageData() {
		//Liste des familles possibles
		this.listeFamille = [{
			id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucune")
		} as Referentiel];

		//Liste des types de prestations possibles
		this.listeTypePrestation = [{
			id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucun")
		} as Referentiel];

		//Liste des comptes charge possibles
		this.listeCompteCharge = [];

		//Liste des codes liasses possibles
		this.listeCodeLiasse = [{
			id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.generalites.infosGenerales.aucun")
		} as Referentiel];

		//Récupération des familles possibles
		this.prestationService.listeFamille().pipe(first()).subscribe(f => {
			//Stockage des familles
			this.listeFamille = this.listeFamille.concat(f);

			//Synchronisation de la famille
			this.syncFamille();
		});

		//Récupération des types de prestations possibles
		this.prestationService.listeTypePrestation().pipe(first()).subscribe(f => {
			//Stockage des types de prestations
			this.listeTypePrestation = this.listeTypePrestation.concat(f);

			//Synchronisation du type de prestation
			this.syncTypePrestation();
		});

		//Récupération des comptes charge possibles
		this.prestationService.listeCompteCharge().pipe(first()).subscribe(f => {
			//Stockage des comptes charge
			this.listeCompteCharge = this.listeCompteCharge.concat(f);

			//Synchronisation du compte charge
			this.syncCompteCharge();
		});

		//Récupération des codes liasses possibles
		this.prestationService.listeCodeLiasse().pipe(first()).subscribe(f => {
			//Stockage des codes liasses
			this.listeCodeLiasse = this.listeCodeLiasse.concat(f);

			//Synchronisation du code liasse
			this.syncCodeLiasse();
		});
	}

	/**
	 * Purge des custom inputs
	 */
	cleanCustomInputs() {
		//Purge des custom inputs
		this.famille.objetTampon.libelle = undefined;
		this.type.objetTampon.libelle = undefined;
		this.liasse.objetTampon.libelle = undefined;
	}

	/**
	 * Synchronisations lors de la mise à jour de la prestation
	 */
	private syncPrestation() {
		//Synchronisation de la famille
		this.syncFamille();

		//Synchronisation du type de prestation
		this.syncTypePrestation();

		//Synchronisation du compte charge
		this.syncCompteCharge();

		//Synchronisation du code liasse
		this.syncCodeLiasse();
	}

	/**
	 * Synchronisation de la famille
	 */
	private syncFamille() {
		//Si une famille est renseignée
		if (this.prestation?.idFamille) {
			//On choisit la famille
			this.selectedFamille = this.listeFamille.find(f => f.id === this.prestation.idFamille);
		} else {
			//Sinon, on choisit la première famille
			this.selectedFamille = this.listeFamille[0];
		}
	}

	/**
	 * Synchronisation du type de prestation
	 */
	private syncTypePrestation() {
		//Si un type de prestation est renseigné
		if (this.prestation?.idTypePrest) {
			//On choisit le type de prestation
			this.selectedTypePrestation = this.listeTypePrestation.find(f => f.id === this.prestation.idTypePrest);
		} else {
			//Sinon, on choisit le premier type de prestation
			this.selectedTypePrestation = this.listeTypePrestation[0];
		}
	}

	/**
	 * Synchronisation du compte charge
	 */
	private syncCompteCharge() {
		//Si un compte charge est renseigné
		if (this.prestation?.compte) {
			//On choisit le compte charge
			this.selectedCompteCharge = this.listeCompteCharge.find(f => f.numero === this.prestation.compte);
		} else {
			//Sinon, forçage de la valeur null
			this.selectedCompteCharge = undefined;
		}
	}

	/**
	 * Synchronisation du code liasse
	 */
	private syncCodeLiasse() {
		//Si un code liasse est renseigné
		if (this.prestation?.idLiasse) {
			//On choisit le code liasse
			this.selectedCodeLiasse = this.listeCodeLiasse.find(f => f.id === this.prestation.idLiasse);
		} else {
			//Sinon, on choisit le premier code liasse
			this.selectedCodeLiasse = this.listeCodeLiasse[0];
		}
	}

	/**
	 * Mise à jour de la famille dans l'objet suite à une sélection
	 */
	onFamilleChange() {
		//Si l'ID est 0 (choix < Aucune >)
		if (this.selectedFamille?.id === 0) {
			//Mise à jour de la famille
			this.prestation.idFamille = 0;
			this.prestation.famille = null;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedFamille?.id?.toString() == PrestationGeneralitesComponent.OBJET_TAMPON ? 0 : this.selectedFamille?.id;

			//Puis, mise à jour de la famille
			this.prestation.idFamille = id;
			this.prestation.famille = new Referentiel();
			this.prestation.famille.id = id;
			this.prestation.famille.libelle = this.selectedFamille?.libelle;
		}
	}

	/**
	 * Mise à jour du type de prestation dans l'objet suite à une sélection
	 */
	onTypePrestationChange() {
		//Si l'ID est 0 (choix < Aucun >)
		if (this.selectedTypePrestation?.id === 0) {
			//Mise à jour du type de prestation
			this.prestation.idTypePrest = 0;
			this.prestation.typePrest = null;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedTypePrestation?.id?.toString() == PrestationGeneralitesComponent.OBJET_TAMPON ? 0 : this.selectedTypePrestation?.id;

			//Puis, mise à jour du type de prestation
			this.prestation.idTypePrest = id;
			this.prestation.typePrest = new Referentiel();
			this.prestation.typePrest.id = id;
			this.prestation.typePrest.libelle = this.selectedTypePrestation?.libelle;
		}
	}

	/**
	 * Mise à jour du compte charge dans l'objet suite à une sélection
	 */
	onCompteChargeChange() {
		//Si le compte charge sélectionné est null
		if (this.selectedCompteCharge == null || this.selectedCompteCharge.numero == null) {
			//Purge du compte charge
			this.prestation.compte = null;
			this.prestation.compteLibelle = null
		} else {
			//Sinon, mise à jour du compte charge
			this.prestation.compte = this.selectedCompteCharge.numero;
			this.prestation.compteLibelle = this.selectedCompteCharge.libelle
		}
	}

	/**
	 * Mise à jour du code liasse dans l'objet suite à une sélection
	 */
	onCodeLiasseChange() {
		//Si l'ID est 0 (choix < Aucun >)
		if (this.selectedCodeLiasse?.id === 0) {
			//Mise à jour du code liasse
			this.prestation.idLiasse = 0;
			this.prestation.liasse = null;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedCodeLiasse?.id?.toString() == PrestationGeneralitesComponent.OBJET_TAMPON ? 0 : this.selectedCodeLiasse?.id;

			//Puis, mise à jour du code liasse
			this.prestation.idLiasse = id;
			this.prestation.liasse = new Referentiel();
			this.prestation.liasse.id = id;
			this.prestation.liasse.libelle = this.selectedCodeLiasse?.libelle;
		}
	}

	/**
	 * Fonction de callback pour l'ajout d'un compte charge
	 */
	addCompteChargeClick: () => void = () => {
		//Ouverture de la boîte de dialogue pour ajouter un compte charge
		this.matDialog.open(AddCompteChargeComponent, {
			width: '60%'
		}).afterClosed().subscribe(compte => {
			if (compte?.numero) {
				//Ajout du compte charge
				this.listeCompteCharge.push(compte);

				//Sélection du compte ajouté
				this.selectedCompteCharge = this.listeCompteCharge.find(f => f.numero === compte.numero);

				//Rafraichissement du compte charge
				this.onCompteChargeChange();
			}
		});
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Résiliation de la souscription
		this.souscription?.unsubscribe();
	}
}